/**
 * Loading data, with the three states a screen actually has to render.
 *
 * Deliberately not a data-fetching library. Every screen here needs the same small thing — a
 * result, a loading flag, an error message and a way to reload after acting — and the interesting
 * behaviour in this app is offline queuing rather than cache invalidation. TanStack Query earns
 * its place once there is shared state to keep fresh across screens; adding it before then would
 * be a dependency doing less than the fifty lines it replaced.
 */

import { useEffect, useRef, useState } from "react";
import { ApiRequestError, describeError } from "./api.js";

export interface AsyncState<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
  /**
   * The HTTP status behind `error`, where there was one.
   *
   * Carried separately because 403 is not a fault: it is somebody following a link to a screen
   * their role does not open, which is ordinary and deserves a different page from a server that
   * fell over. The message alone cannot be told apart from the others.
   */
  errorStatus: number | null;
  reload: () => void;
}

export function useAsync<T>(load: () => Promise<T>, deps: unknown[]): AsyncState<T> {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [errorStatus, setErrorStatus] = useState<number | null>(null);
  const [nonce, setNonce] = useState(0);

  // The loader closes over values the caller controls, so the dependency list is theirs to declare.
  // It is held in a ref rather than memoised: a generic hook cannot present the caller's array as
  // a literal, and the effect below keys off `deps` directly, which is the thing that actually
  // decides when to reload.
  const latest = useRef(load);
  const key = JSON.stringify(deps);

  // Assigned inside an effect rather than during render, so the ref is only ever written where
  // writing it is defined behaviour.
  useEffect(() => {
    latest.current = load;
  });

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    setErrorStatus(null);

    latest
      .current()
      .then((result) => {
        if (!cancelled) setData(result);
      })
      .catch((cause: unknown) => {
        if (cancelled) return;
        setError(describeError(cause));
        setErrorStatus(cause instanceof ApiRequestError ? cause.status : null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [key, nonce]);

  return { data, loading, error, errorStatus, reload: () => setNonce((value) => value + 1) };
}

/**
 * Run an action and report its outcome.
 *
 * Actions are separated from loads because they fail differently: a failed load means a screen
 * cannot be drawn, while a failed action means a specific thing did not happen and the person is
 * still looking at the screen that tried it.
 */
export function useAction(): {
  busy: boolean;
  error: string | null;
  clear: () => void;
  run: <T>(action: () => Promise<T>) => Promise<T | undefined>;
} {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  return {
    busy,
    error,
    clear: () => setError(null),
    run: async <T>(action: () => Promise<T>) => {
      setBusy(true);
      setError(null);
      try {
        return await action();
      } catch (cause) {
        setError(describeError(cause));
        return undefined;
      } finally {
        setBusy(false);
      }
    },
  };
}
